/**
 * Token Refresh Queue
 *
 * Prevents race conditions when multiple requests fail with 401 simultaneously.
 * Ensures only ONE token refresh request is made at a time, while other
 * requests wait for the result.
 *
 * NOTE: Tokens are now stored in httpOnly cookies (server-side), not localStorage.
 * Browser automatically sends/receives cookies with requests.
 */

import { AUTH_ENDPOINTS } from '@/constants/auth';

type RefreshPromise = Promise<boolean>;

class TokenRefreshQueue {
  private refreshPromise: RefreshPromise | null = null;
  private isRefreshing = false;

  /**
   * Performs token refresh
   * Multiple simultaneous calls will share the same refresh promise
   * Returns true if refresh succeeded, false otherwise
   */
  async refresh(): Promise<boolean> {
    // If already refreshing, wait for that promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start refresh process
    this.isRefreshing = true;
    this.refreshPromise = this.performRefresh();

    try {
      const success = await this.refreshPromise;
      return success;
    } finally {
      // Clean up
      this.refreshPromise = null;
      this.isRefreshing = false;
    }
  }

  /**
   * Performs the actual token refresh
   * Cookies are sent/received automatically by browser
   */
  private async performRefresh(): Promise<boolean> {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

      // Browser automatically sends refreshToken cookie
      const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.REFRESH}`, {
        method: 'POST',
        credentials: 'include', // Important: send cookies
        headers: {
          'Content-Type': 'application/json',
          'X-Kaive-Audience': 'client',
        },
        body: JSON.stringify({}), // Send empty object to satisfy Fastify
      });

      if (!response.ok) {
        return false;
      }

      // New tokens are set in cookies by server
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Checks if a refresh is currently in progress
   */
  isRefreshInProgress(): boolean {
    return this.isRefreshing;
  }
}

// Singleton instance
export const tokenRefreshQueue = new TokenRefreshQueue();
